import { FrameData } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { LayerRenderer } from './LayerRenderer';

interface Star {
  x: number;
  y: number;
  z: number;
}

export class StarfieldRenderer implements LayerRenderer {
  private stars: Star[] = [];
  private beatPulse = new BeatPulse(0.88);
  private smoothSpeed = new SmoothedValue(0.1);
  private currentCount = 0;

  init(): void {}

  private createStar(randomDepth = false): Star {
    return {
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: randomDepth ? Math.random() : 1,
    };
  }

  private ensureStarCount(count: number): void {
    if (count === this.currentCount && this.stars.length === count) return;
    this.currentCount = count;
    while (this.stars.length < count) {
      this.stars.push(this.createStar(true));
    }
    if (this.stars.length > count) {
      this.stars.length = count;
    }
  }

  render(
    ctx: CanvasRenderingContext2D,
    frame: FrameData,
    settings: Record<string, number>,
    width: number,
    height: number,
  ): void {
    const { overallEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const starCount = Math.round(settings.starCount ?? 400);
    const baseSpeed = settings.baseSpeed ?? 0.3;

    this.ensureStarCount(starCount);
    this.beatPulse.update(isBeat, beatIntensity);

    const speed = this.smoothSpeed.update(
      baseSpeed + overallEnergy * 0.8 + this.beatPulse.value * 2.5
    );

    const cx = width / 2;
    const cy = height / 2;
    const dt = deltaTime / 16.67;

    for (const star of this.stars) {
      const prevSX = cx + (star.x / star.z) * cx;
      const prevSY = cy + (star.y / star.z) * cy;

      star.z -= speed * 0.01 * dt;

      if (star.z <= 0.001) {
        Object.assign(star, this.createStar());
        continue;
      }

      const sx = cx + (star.x / star.z) * cx;
      const sy = cy + (star.y / star.z) * cy;

      if (sx < -20 || sx > width + 20 || sy < -20 || sy > height + 20) {
        Object.assign(star, this.createStar());
        continue;
      }

      const brightness = Math.min(1, (1 - star.z) * 1.5);
      const size = Math.max(0.5, (1 - star.z) * 3);

      // Streak line
      ctx.strokeStyle = `rgba(200, 220, 255, ${brightness * 0.7})`;
      ctx.lineWidth = size * 0.6;
      ctx.beginPath();
      ctx.moveTo(prevSX, prevSY);
      ctx.lineTo(sx, sy);
      ctx.stroke();

      // Star dot
      ctx.beginPath();
      ctx.arc(sx, sy, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(220, 235, 255, ${brightness})`;
      ctx.fill();
    }
  }

  resize(): void {}

  destroy(): void {
    this.stars = [];
  }
}
